import { useState, useRef, useCallback, type ChangeEvent, type DragEvent } from 'react';
import { Upload, X, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import Button from './Button';

export interface FileUploadProps {
  onFilesSelected: (files: File[]) => void;
  onFileRemove?: (file: File) => void;
  supportedFormats?: string[]; // e.g., ['pdf', 'docx', 'txt']
  maxFileSize?: number; // in bytes
  allowMultiple?: boolean;
  className?: string;
  disabled?: boolean;
  showFileList?: boolean;
  autoUpload?: boolean;
  onUploadComplete?: (results: UploadResult[]) => void;
  uploadHandler?: (file: File) => Promise<any>;
}

export interface UploadResult {
  file: File;
  success: boolean;
  error?: string;
  data?: any;
}

interface FileWithValidation {
  file: File;
  id: string;
  errors: string[];
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const FileUpload: React.FC<FileUploadProps> = ({
  onFilesSelected,
  onFileRemove,
  supportedFormats = [],
  maxFileSize = 50 * 1024 * 1024, // Default 50MB
  allowMultiple = true,
  className = '',
  disabled = false,
  showFileList = true,
  autoUpload = false,
  onUploadComplete,
  uploadHandler,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileWithValidation[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string[] => {
    const errors: string[] = [];

    // Validate file size
    if (file.size > maxFileSize) {
      errors.push(`File size exceeds ${formatFileSize(maxFileSize)}`);
    }

    // Validate file type
    if (supportedFormats.length > 0) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
      if (!supportedFormats.includes(fileExtension)) {
        errors.push(`File type '.${fileExtension}' not supported. Allowed: ${supportedFormats.join(', ')}`);
      }
    }

    return errors;
  }, [maxFileSize, supportedFormats]);

  const processFiles = useCallback((files: File[]) => {
    const newFiles: FileWithValidation[] = files.map(file => ({
      file,
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      errors: validateFile(file),
    }));

    if (allowMultiple) {
      setSelectedFiles(prev => [...prev, ...newFiles]);
    } else {
      setSelectedFiles(newFiles.slice(0, 1));
    }

    // Notify parent of selected files (only valid ones)
    const validFiles = newFiles.filter(f => f.errors.length === 0).map(f => f.file);
    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }

    // Auto upload if enabled
    if (autoUpload && uploadHandler && validFiles.length > 0) {
      handleAutoUpload(validFiles);
    }
  }, [allowMultiple, validateFile, onFilesSelected, autoUpload, uploadHandler]);

  const handleAutoUpload = async (files: File[]) => {
    if (!uploadHandler) return;

    const results: UploadResult[] = [];
    for (const file of files) {
      try {
        const data = await uploadHandler(file);
        results.push({ file, success: true, data });
      } catch (error: any) {
        results.push({ file, success: false, error: error.message });
      }
    }

    if (onUploadComplete) {
      onUploadComplete(results);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      processFiles(files);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleRemoveFile = (id: string) => {
    const fileToRemove = selectedFiles.find(f => f.id === id);
    setSelectedFiles(prev => prev.filter(f => f.id !== id));

    if (fileToRemove && onFileRemove) {
      onFileRemove(fileToRemove.file);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const getAcceptAttribute = () => {
    if (supportedFormats.length === 0) return undefined;
    return supportedFormats.map(ext => `.${ext}`).join(',');
  };

  const hasValidFiles = selectedFiles.some(f => f.errors.length === 0);
  const hasInvalidFiles = selectedFiles.some(f => f.errors.length > 0);

  return (
    <div className={`w-full ${className}`}>
      {/* Drop Zone */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-50'}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileChange}
          multiple={allowMultiple}
          accept={getAcceptAttribute()}
          disabled={disabled}
        />
        <Upload className={`w-7 h-7 mx-auto mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
        <p className="text-gray-700 font-medium">
          {isDragging ? 'Drop files here' : 'Drag and drop files here'}
        </p>
        <p className="text-gray-500 text-sm">
          or <span className="text-blue-500 hover:underline">click to browse</span>
        </p>
        <p className="text-xs text-gray-400 mt-2">
          {supportedFormats.length > 0 && (
            <>
              Accepted formats: {supportedFormats.map(f => `.${f}`).join(', ')}
              <br />
            </>
          )}
          Max file size: {formatFileSize(maxFileSize)}
          {!allowMultiple && <>, §Single file only</>}
        </p>
      </div>

      {/* File List */}
      {showFileList && selectedFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">
              Selected Files ({selectedFiles.length})
            </p>
            {selectedFiles.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFiles([])}
                className="text-gray-500 hover:text-gray-700"
              >
                Clear All
              </Button>
            )}
          </div>

          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
            {selectedFiles.map((fileObj) => (
              <div
                key={fileObj.id}
                className="flex items-center justify-between p-3 hover:bg-gray-50"
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate" title={fileObj.file.name}>
                      {fileObj.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(fileObj.file.size)}
                    </p>
                    {fileObj.errors.length > 0 && (
                      <div className="mt-1 space-y-1">
                        {fileObj.errors.map((error, idx) => (
                          <div key={idx} className="flex items-center space-x-1 text-xs text-red-600">
                            <AlertCircle className="w-3 h-3" />
                            <span>{error}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {fileObj.errors.length === 0 && (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(fileObj.id);
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                    title="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          {(hasValidFiles || hasInvalidFiles) && (
            <div className="flex items-center justify-between text-xs text-gray-600 pt-2">
              {hasValidFiles && (
                <span className="flex items-center text-green-600">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {selectedFiles.filter(f => f.errors.length === 0).length} valid file(s)
                </span>
              )}
              {hasInvalidFiles && (
                <span className="flex items-center text-red-600">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  {selectedFiles.filter(f => f.errors.length > 0).length} invalid file(s)
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
